import produce from "immer"
import type { LocationLookupResponse } from "../../models"
import type { PagingActions, PagingOptions } from "./../../models/types"
import {
  TOPOGRAPHY_APPEND_LIST,
  TOPOGRAPHY_LOAD,
  TOPOGRAPHY_LOAD_API_CALL_COUNT,
  TOPOGRAPHY_LOAD_LIST,
  TOPOGRAPHY_LOAD_PAGING_OPTIONS,
  TOPOGRAPHY_LOAD_SEARCH_RESULTS,
  TOPOGRAPHY_REMOVE_FROM_LIST,
  TOPOGRAPHY_SET_COORDS_FILTER
} from "./topography.actions"
import type { ITopographyState, Topography, TopographyActions } from "./topography.types"

export const topographyInitialState: ITopographyState = {
  list: [],
  pagingOptions: {
    currentPage: 0,
    totalPages: 0,
    pageSize: 100
  },
  coordsFilter: false
}

export function topographyReducer(
  currentState: ITopographyState | undefined,
  action: TopographyActions | PagingActions
): ITopographyState {
  const state = currentState ?? topographyInitialState
  switch (action.type) {
    case TOPOGRAPHY_LOAD_LIST:
      return setList(state, action.payload as Topography[])
    case TOPOGRAPHY_APPEND_LIST:
      return appendList(state, action.payload as Topography[])
    case TOPOGRAPHY_LOAD:
      return loadTopography(state, action.payload as Topography)
    case TOPOGRAPHY_LOAD_API_CALL_COUNT:
      return setCount(state, action.payload as number)
    case TOPOGRAPHY_LOAD_SEARCH_RESULTS:
      return setResults(state, action.payload as LocationLookupResponse)
    case TOPOGRAPHY_LOAD_PAGING_OPTIONS:
      return setPaging(state, action.payload as PagingOptions)
    case TOPOGRAPHY_SET_COORDS_FILTER:
      return setCoordsFilter(state, action.payload as boolean)
    case TOPOGRAPHY_REMOVE_FROM_LIST:
      return removeFromList(state, action.payload as Topography)
    default:
      return state
  }
}

function setList(state: ITopographyState, data: Topography[]): ITopographyState {
  return produce(state, (draftState: ITopographyState) => {
    draftState.list = data
  })
}

function appendList(state: ITopographyState, data: Topography[]): ITopographyState {
  return produce(state, (draftState: ITopographyState) => {
    draftState.list.push(...data)
  })
}

function setCoordsFilter(state: ITopographyState, coordsFilter: boolean): ITopographyState {
  return produce(state, (draftState: ITopographyState) => {
    draftState.coordsFilter = coordsFilter
  })
}

function loadTopography(state: ITopographyState, topography?: Topography): ITopographyState {
  return produce(state, (draftState: ITopographyState) => {
    draftState.item = topography

    if (!topography || !topography.id) return

    const listContainsItem: boolean = draftState.list.some((t: Topography) => t.id === topography.id)

    if (listContainsItem) {
      draftState.list = draftState.list.map((t: Topography) => {
        if (t.id === topography.id) return topography

        return t
      })
    } else {
      draftState.list.push(topography)
    }
  })
}

function setCount(state: ITopographyState, count: number): ITopographyState {
  return produce(state, (draftState: ITopographyState) => {
    draftState.lookupCount = count
  })
}

function setResults(state: ITopographyState, results: LocationLookupResponse): ITopographyState {
  return produce(state, (draftState: ITopographyState) => {
    draftState.locationResults = results.response
    draftState.lookupCount = results.apiCallCount
  })
}

function setPaging(state: ITopographyState, paging: PagingOptions): ITopographyState {
  return produce(state, (draftState: ITopographyState) => {
    draftState.pagingOptions = paging
  })
}

function removeFromList(state: ITopographyState, topography: Topography): ITopographyState {
  return produce(state, (draftState: ITopographyState) => {
    draftState.list = draftState.list.filter((t: Topography) => t.id !== topography.id)
  })
}
